import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { addPreviousMessages } from "../../../../redux/slices/chatSlice";
import type { AppDispatch } from "../../../../redux/store";
import messageCacheDB from "../../../../services/messageCacheDB";
import { FEATURE_FLAGS } from "../../../../constants";
import { messageGraph } from "../../../../services/messageGraph";
import type { ChatMessage } from "../../../../utils/computeDivider";

const PAGE_LIMIT = 30;
const ALWAYS_ALLOW_API_PAGINATION = true;
const BOTTOM_THRESHOLD = 50;

interface Chat {
  id: string;
  [key: string]: unknown;
}

export interface UseScrollEngineOptions {
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  messagesListRef: React.MutableRefObject<ChatMessage[]>;
  selectedChatRef: React.MutableRefObject<Chat | null>;
  messageRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  // scrollAnchorRef is owned by the component so getChat can also write to it.
  scrollAnchorRef: React.MutableRefObject<"bottom" | string | null>;
  messagesList: ChatMessage[];
  loading: boolean;
  hasMore: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  currentChatId: string | undefined;
  userId: string | null;
  getChat: (id: string | undefined, page: number) => Promise<unknown>;
  dispatch: AppDispatch;
  // From useUnreadDivider
  unreadBadgeCountRef: React.MutableRefObject<number>;
  dividerClearTimerRef: React.MutableRefObject<NodeJS.Timeout | null>;
  persistRead: (
    chatId: string,
    timestamp: string,
    remainingUnreadCount?: number,
  ) => void;
  persistReadRef: React.MutableRefObject<
    (chatId: string, timestamp: string, remainingUnreadCount?: number) => void
  >;
  setUnreadBadgeCount: React.Dispatch<React.SetStateAction<number>>;
  setFirstUnreadMsgId: React.Dispatch<React.SetStateAction<string | null>>;
  setDividerUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  clearDivider: (immediate?: boolean) => void;
}

export interface UseScrollEngineReturn {
  isAtBottom: boolean;
  setIsAtBottom: React.Dispatch<React.SetStateAction<boolean>>;
  isAtBottomRef: React.MutableRefObject<boolean>;
  autoScrollRef: React.MutableRefObject<boolean>;
  shouldPinRef: React.MutableRefObject<boolean>;
  sendTriggeredRef: React.MutableRefObject<boolean>;
  userScrolledUpRef: React.MutableRefObject<boolean>;
  scrollToBottom: (immediate?: boolean) => void;
  handleScrollToBottomClick: () => void;
  loadMoreMessage: () => Promise<void>;
}

export function useScrollEngine({
  messagesContainerRef,
  messagesListRef,
  selectedChatRef,
  messageRefs,
  scrollAnchorRef,
  messagesList,
  loading,
  hasMore,
  page,
  setPage,
  currentChatId,
  userId,
  getChat,
  dispatch,
  unreadBadgeCountRef,
  dividerClearTimerRef,
  persistRead,
  persistReadRef,
  setUnreadBadgeCount,
  setFirstUnreadMsgId,
  setDividerUnreadCount,
  clearDivider,
}: UseScrollEngineOptions): UseScrollEngineReturn {
  const [isAtBottom, setIsAtBottom] = useState(true);
  const isAtBottomRef = useRef(true);
  const autoScrollRef = useRef(false);
  const shouldPinRef = useRef(false);
  const sendTriggeredRef = useRef(false);
  const userScrolledUpRef = useRef(false);

  const prevScrollHeightRef = useRef(0);
  const isPaginatingRef = useRef(false);
  const lastKnownScrollHeightRef = useRef(0);
  const scrollRafRef = useRef<number | null>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = useCallback(
    (immediate = false) => {
      autoScrollRef.current = true;
      sendTriggeredRef.current = immediate;
      shouldPinRef.current = true;
      userScrolledUpRef.current = false;
      isAtBottomRef.current = true;
      unreadBadgeCountRef.current = 0;
      setIsAtBottom(true);
      setUnreadBadgeCount(0);

      const chatId = selectedChatRef.current?.id;
      const latestTs =
        messagesListRef.current[messagesListRef.current.length - 1]?.createdAt ??
        new Date().toISOString();
      if (chatId) persistRead(chatId, latestTs);

      clearDivider(immediate);
    },
    [
      persistRead,
      clearDivider,
      setUnreadBadgeCount,
      selectedChatRef,
      messagesListRef,
      unreadBadgeCountRef,
    ],
  );

  const handleScrollToBottomClick = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container)
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    isAtBottomRef.current = true;
    shouldPinRef.current = true;
    userScrolledUpRef.current = false;
    unreadBadgeCountRef.current = 0;
    setIsAtBottom(true);
    setUnreadBadgeCount(0);

    const chatId = selectedChatRef.current?.id;
    const latestTs =
      messagesListRef.current[messagesListRef.current.length - 1]?.createdAt ??
      new Date().toISOString();
    if (chatId) persistRead(chatId, latestTs);
    clearDivider(false);
  }, [
    persistRead,
    clearDivider,
    setUnreadBadgeCount,
    messagesContainerRef,
    selectedChatRef,
    messagesListRef,
    unreadBadgeCountRef,
  ]);

  const loadMoreMessage = useCallback(async () => {
    const chatContainer = messagesContainerRef.current;
    if (!chatContainer || loading || !hasMore || messagesList.length < 10) return;

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(async () => {
      if (chatContainer.scrollTop > 50) return;

      prevScrollHeightRef.current = chatContainer.scrollHeight;
      isPaginatingRef.current = true;

      if (userId && currentChatId) {
        const oldest = messagesListRef.current[0];
        if (oldest?.createdAt) {
          try {
            const idbPage = await messageCacheDB.getMessagesBefore(
              userId,
              currentChatId,
              oldest.createdAt,
              PAGE_LIMIT,
            );
            if (idbPage.length > 0) {
              const existingIds = new Set(
                messagesListRef.current.map((m) => String(m.id)),
              );
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const freshIdb = idbPage.filter((m: any) => !existingIds.has(String(m.id)));
              if (freshIdb.length > 0) {
                if (FEATURE_FLAGS.USE_CRDT_ENGINE) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  freshIdb.forEach((m: any) => messageGraph.mirror(currentChatId, m));
                }
                dispatch(addPreviousMessages(freshIdb));
              }
            }
          } catch {
            // IDB unavailable — API below will serve data
          }
        }
      }

      if (ALWAYS_ALLOW_API_PAGINATION) {
        await getChat(currentChatId, page + 1);
        setPage((prev) => prev + 1);
      }
    }, 150);
  }, [
    loading,
    hasMore,
    messagesList.length,
    currentChatId,
    page,
    userId,
    getChat,
    setPage,
    dispatch,
    messagesContainerRef,
    messagesListRef,
  ]);

  // Before-paint: pagination scroll restore + anchor jumps only.
  useLayoutEffect(() => {
    const chatContainer = messagesContainerRef.current;
    if (!chatContainer || messagesList.length === 0) return;

    if (isPaginatingRef.current) {
      const diff = chatContainer.scrollHeight - prevScrollHeightRef.current;
      if (diff > 0) chatContainer.scrollTop += diff;
      isPaginatingRef.current = false;
      lastKnownScrollHeightRef.current = chatContainer.scrollHeight;
      return;
    }

    const anchor = scrollAnchorRef.current;
    if (anchor) {
      scrollAnchorRef.current = null;
      if (anchor === "bottom") {
        chatContainer.scrollTop = chatContainer.scrollHeight;
        isAtBottomRef.current = true;
        shouldPinRef.current = true;
        userScrolledUpRef.current = false;
        setIsAtBottom(true);
      } else {
        const el = messageRefs.current.get(anchor);
        if (el) {
          el.scrollIntoView({ block: "start" });
          isAtBottomRef.current = false;
          shouldPinRef.current = false;
          userScrolledUpRef.current = true;
          setIsAtBottom(false);
        } else {
          chatContainer.scrollTop = chatContainer.scrollHeight;
          isAtBottomRef.current = true;
          shouldPinRef.current = true;
          setIsAtBottom(true);
        }
      }
      autoScrollRef.current = false;
      lastKnownScrollHeightRef.current = chatContainer.scrollHeight;
    }
  }, [messagesList, messagesContainerRef, messageRefs, scrollAnchorRef]);

  // MutationObserver fires synchronously when React appends a new message node —
  // before any scroll event fires and before the browser paints.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const observer = new MutationObserver((mutations) => {
      const added = mutations.some((m) => m.addedNodes.length > 0);
      if (!added) return;

      if (isAtBottomRef.current || autoScrollRef.current) {
        const wasSendTriggered = sendTriggeredRef.current;
        autoScrollRef.current = false;
        sendTriggeredRef.current = false;
        container.scrollTop = container.scrollHeight;
        isAtBottomRef.current = true;
        shouldPinRef.current = true;
        setIsAtBottom(true);
        unreadBadgeCountRef.current = 0;
        setUnreadBadgeCount(0);

        const chatId = selectedChatRef.current?.id;
        const latestTs =
          messagesListRef.current[messagesListRef.current.length - 1]
            ?.createdAt ?? new Date().toISOString();
        if (chatId) persistReadRef.current(chatId, latestTs);

        if (dividerClearTimerRef.current)
          clearTimeout(dividerClearTimerRef.current);
        if (wasSendTriggered) {
          setFirstUnreadMsgId(null);
          setDividerUnreadCount(0);
        } else {
          dividerClearTimerRef.current = setTimeout(() => {
            setFirstUnreadMsgId(null);
            setDividerUnreadCount(0);
          }, 3000);
        }
      }
    });

    observer.observe(container, { childList: true });
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register loadMoreMessage as scroll listener — re-registers when it changes.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", loadMoreMessage);
    return () => container.removeEventListener("scroll", loadMoreMessage);
  }, [loadMoreMessage, loading, page, messagesContainerRef]);

  // rAF-throttled scroll handler — registers once on mount.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      // Update ref immediately — socket handlers must never read a stale value.
      const atBottomNow =
        container.scrollHeight - container.scrollTop - container.clientHeight <=
        BOTTOM_THRESHOLD;
      isAtBottomRef.current = atBottomNow;

      if (scrollRafRef.current !== null) return;
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;

        const atBottom =
          container.scrollHeight -
            container.scrollTop -
            container.clientHeight <=
          BOTTOM_THRESHOLD;

        isAtBottomRef.current = atBottom;
        setIsAtBottom(atBottom);

        if (atBottom) {
          unreadBadgeCountRef.current = 0;
          setUnreadBadgeCount(0);
          shouldPinRef.current = true;
          userScrolledUpRef.current = false;

          const chatId = selectedChatRef.current?.id;
          const latestTs =
            messagesListRef.current[messagesListRef.current.length - 1]
              ?.createdAt ?? new Date().toISOString();
          if (chatId) persistReadRef.current(chatId, latestTs);

          if (dividerClearTimerRef.current)
            clearTimeout(dividerClearTimerRef.current);
          dividerClearTimerRef.current = setTimeout(() => {
            setFirstUnreadMsgId(null);
            setDividerUnreadCount(0);
          }, 3000);
        } else {
          if (dividerClearTimerRef.current) {
            clearTimeout(dividerClearTimerRef.current);
            dividerClearTimerRef.current = null;
          }
          shouldPinRef.current = false;
          userScrolledUpRef.current = true;
        }
      });
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      container.removeEventListener("scroll", onScroll);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isAtBottom,
    setIsAtBottom,
    isAtBottomRef,
    autoScrollRef,
    shouldPinRef,
    sendTriggeredRef,
    userScrolledUpRef,
    scrollToBottom,
    handleScrollToBottomClick,
    loadMoreMessage,
  };
}
